import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {authHeader} from '../../utils/headers';

const API_BASE = import.meta.env.VITE_APIURL || '';

// Binding actes <-> Form (FormActe). SearchFilter exact sur `form`.
// Conventions RTK Query : Accept ld+json force, body JSON.stringify soi-meme,
// merge-patch+json en PATCH, ld+json en POST.
//
// Mutations OPTIMISTES (onQueryStarted) : le cache `getByForm` est patché
// immédiatement au clic (l'UI bouge tout de suite), rollback si l'API échoue,
// et l'invalidation resynchronise depuis le serveur derrière. Les entrées
// créées optimistiquement portent `_pending: true` (pas encore d'id serveur) :
// l'UI doit désactiver leurs actions jusqu'au refetch.
export const formActesApi = createApi({
    reducerPath: 'formActesApi',
    baseQuery: fetchBaseQuery({
        baseUrl: API_BASE,
        prepareHeaders: (headers) => {
            const token = authHeader();
            if (typeof token === 'string' && token) {
                headers.set('authorization', token);
            }
            headers.set('Accept', 'application/ld+json');
            return headers;
        },
    }),
    tagTypes: ['FormActe'],
    endpoints: (builder) => ({
        getByForm: builder.query({
            query: (formIri) => `/form_actes?form=${encodeURIComponent(formIri)}`,
            transformResponse: (r) => r?.['hydra:member'] ?? (Array.isArray(r) ? r : []),
            providesTags: (result, error, formIri) => [{type: 'FormActe', id: formIri}],
        }),
        createFormActe: builder.mutation({
            query: ({formIri, ...body}) => ({
                url: '/form_actes',
                method: 'POST',
                headers: {'Content-Type': 'application/ld+json'},
                body: JSON.stringify(body),
            }),
            async onQueryStarted({formIri, ...body}, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    formActesApi.util.updateQueryData('getByForm', formIri, (draft) => {
                        draft.push({id: null, _pending: true, ...body});
                    })
                );
                try { await queryFulfilled; } catch { patchResult.undo(); }
            },
            invalidatesTags: (result, error, {formIri}) => [{type: 'FormActe', id: formIri}],
        }),
        updateFormActe: builder.mutation({
            query: ({id, formIri, ...patch}) => ({
                url: `/form_actes/${id}`,
                method: 'PATCH',
                headers: {'Content-Type': 'application/merge-patch+json'},
                body: JSON.stringify(patch),
            }),
            async onQueryStarted({id, formIri, ...patch}, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    formActesApi.util.updateQueryData('getByForm', formIri, (draft) => {
                        const item = draft.find((fa) => fa.id === id);
                        if (item) Object.assign(item, patch);
                    })
                );
                try { await queryFulfilled; } catch { patchResult.undo(); }
            },
            invalidatesTags: (result, error, {formIri}) => [{type: 'FormActe', id: formIri}],
        }),
        deleteFormActe: builder.mutation({
            query: ({id}) => ({url: `/form_actes/${id}`, method: 'DELETE'}),
            async onQueryStarted({id, formIri}, {dispatch, queryFulfilled}) {
                const patchResult = dispatch(
                    formActesApi.util.updateQueryData('getByForm', formIri, (draft) => {
                        const idx = draft.findIndex((fa) => fa.id === id);
                        if (idx !== -1) draft.splice(idx, 1);
                    })
                );
                try { await queryFulfilled; } catch { patchResult.undo(); }
            },
            invalidatesTags: (result, error, {formIri}) => [{type: 'FormActe', id: formIri}],
        }),
    }),
});

export const {
    useGetByFormQuery,
    useCreateFormActeMutation,
    useUpdateFormActeMutation,
    useDeleteFormActeMutation,
} = formActesApi;
